/**
 * Modal liviano para registrar un movimiento extra al aseador desde el
 * panel admin (`/finanzas-aseador-admin`). Crea una fila en `gastos` vía
 * el atajo `POST /api/finanzas/aseador/:id/movimientos` con defaults
 * inteligentes (ver backend `crearMovimientoAseador`).
 *
 * Por qué un modal aparte y no abrir el formulario completo de
 * `/finanzas-empresa`: aquí el contexto ya está fijo (el aseador del path),
 * el admin solo necesita decidir monto + concepto + dirección + fecha +
 * contraparte. El formulario completo es para los gastos contables del
 * panel empresa.
 *
 * Estado del row creado: `pendiente` por contrato. El cron del lunes
 * (Fase 3) recordará al admin aprobar; mientras tanto el admin puede
 * aprobar manualmente desde el detalle del aseador.
 *
 * Round 2026-04-29 — agregamos el selector de **contraparte** (AF /
 * Propiedad / Externo). Sin esta dimensión el sistema asumía siempre AF
 * y no podía modelar el caso "el propietario paga al aseador a través de
 * AF" — ahora ese flujo crea el row con `propiedad_id` seteado y
 * `rol_af='intermediario'`, así aparece en la liquidación al cliente
 * (`/finanzas-clientes`) sin contaminar la utilidad AF.
 */

import { useEffect, useState } from "react";
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Typography,
} from "antd";
import type { Dayjs } from "dayjs";
import { finanzasApi, propiedadesApi } from "../../services/api";
import type { ConceptoAseador } from "../../services/finanzas.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { ahoraChile } from "../../utils/fechaChile";

const { Text } = Typography;

/**
 * Opciones del enum `concepto_aseador` con label legible y un hint corto
 * que ayuda al admin a elegir bien. El backend valida el code; los labels
 * son solo UX.
 */
const CONCEPTO_OPTIONS: Array<{
  value: ConceptoAseador;
  label: string;
  hint: string;
  defaultSigno: 1 | -1;
}> = [
  {
    value: "adelanto",
    label: "Adelanto",
    hint: "Dinero que el aseador recibe a cuenta del próximo pago.",
    defaultSigno: 1,
  },
  {
    value: "bono_extra",
    label: "Bono extra",
    hint: "Reconocimiento puntual no atado a un aseo específico.",
    defaultSigno: 1,
  },
  {
    value: "descuento_disciplinario",
    label: "Descuento disciplinario",
    hint: "Resta por incumplimiento o sanción interna.",
    defaultSigno: -1,
  },
  {
    value: "reembolso_gasto",
    label: "Reembolso de gasto",
    hint: "Aseador adelantó plata por AF (transporte, materiales) y se le devuelve.",
    defaultSigno: 1,
  },
  {
    value: "propina",
    label: "Propina",
    hint: "Tip de huésped o cliente, registrada vía AF.",
    defaultSigno: 1,
  },
  {
    value: "multa",
    label: "Multa",
    hint: "Cargo punitivo formal — diferente a 'descuento_disciplinario' por gravedad.",
    defaultSigno: -1,
  },
  {
    value: "otro",
    label: "Otro",
    hint: "Movimiento no categorizado (admin describe en observaciones).",
    defaultSigno: 1,
  },
];

/**
 * Posibles "contrapartes" del flujo. El default es AF (compatibilidad
 * con el comportamiento original del modal). Cada kind dispara una UI
 * distinta (ver `<ContraparteFields />` abajo).
 */
type ContraparteKind = "af" | "propiedad" | "externo";

interface FormValues {
  monto: number;
  signo: 1 | -1;
  concepto: ConceptoAseador;
  descripcion: string;
  fecha: Dayjs;
  contraparteKind: ContraparteKind;
  contrapartePropiedadId?: number;
  contraparteNombreExterno?: string;
}

export interface RegistrarMovimientoModalProps {
  open: boolean;
  aseadorId: number | null;
  aseadorNombre?: string;
  onClose: () => void;
  /**
   * Llamado tras un POST exitoso. El padre debería refetchear el panel
   * (los WS también disparan refresh, pero llamarlo es defensa adicional).
   */
  onCreated: () => void;
}

interface PropiedadOption {
  value: number;
  label: string;
}

export default function RegistrarMovimientoModal({
  open,
  aseadorId,
  aseadorNombre,
  onClose,
  onCreated,
}: RegistrarMovimientoModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);

  // Lista de propiedades para el dropdown cuando contraparte=propiedad.
  // Carga lazy: sólo al abrir el modal por primera vez. Si admin nunca
  // elige "Propiedad", no pegamos al backend.
  const [propiedades, setPropiedades] = useState<PropiedadOption[]>([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [propsLoaded, setPropsLoaded] = useState(false);

  const ensurePropiedadesLoaded = async () => {
    if (propsLoaded || loadingProps) return;
    setLoadingProps(true);
    const resp = await propiedadesApi.getAll();
    if (resp && Array.isArray(resp.data)) {
      setPropiedades(
        resp.data.map((p) => ({
          value: Number(p.id),
          label: formatPropertyName(p.nombrepropiedad) || `Propiedad #${p.id}`,
        })),
      );
      setPropsLoaded(true);
    }
    setLoadingProps(false);
  };

  // Cuando admin elige el concepto, sugerimos un signo por defecto que
  // suele encajar (adelanto/bono → +; multa/descuento → −). Igual el admin
  // puede cambiarlo manualmente con el Radio.
  const handleConceptoChange = (value: ConceptoAseador) => {
    const opt = CONCEPTO_OPTIONS.find((o) => o.value === value);
    if (opt) form.setFieldValue("signo", opt.defaultSigno);
  };

  // Si admin cambia a "Propiedad", precargamos la lista — UX directa, no
  // queremos que vea un Select vacío con loading mientras tipea.
  const handleContraparteChange = (kind: ContraparteKind) => {
    if (kind === "propiedad") void ensurePropiedadesLoaded();
  };

  const handleOk = async () => {
    if (!aseadorId) return;
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Construir el shape que el backend espera. Sólo enviamos el campo
    // de contraparte cuando NO es el default (para mantener el payload
    // mínimo y compatible con eventuales clientes legacy).
    const contraparte =
      values.contraparteKind === "af"
        ? undefined
        : values.contraparteKind === "propiedad"
        ? {
            kind: "propiedad" as const,
            propiedadId: Number(values.contrapartePropiedadId),
          }
        : {
            kind: "externo" as const,
            nombreExterno: values.contraparteNombreExterno?.trim() || undefined,
          };

    setSubmitting(true);
    const resp = await finanzasApi.crearMovimientoAseador(aseadorId, {
      monto: Math.round(values.monto),
      signo: values.signo,
      concepto: values.concepto,
      descripcion: values.descripcion,
      fecha: values.fecha.format("YYYY-MM-DD"),
      contraparte,
    });
    setSubmitting(false);
    if (resp) {
      form.resetFields();
      onCreated();
      onClose();
    }
  };

  const conceptoActual = Form.useWatch("concepto", form);
  const conceptoHint = CONCEPTO_OPTIONS.find(
    (o) => o.value === conceptoActual,
  )?.hint;

  // Watch del kind de contraparte para renderizar/esconder los inputs
  // condicionales (Select de propiedades / Input de nombre externo).
  const contraparteKind = Form.useWatch("contraparteKind", form) as
    | ContraparteKind
    | undefined;
  const signoActual = Form.useWatch("signo", form) as 1 | -1 | undefined;

  // Si el modal se abre y aún no cargamos propiedades, las dejamos listas
  // por si admin las necesita — barato y elimina el flicker.
  useEffect(() => {
    if (open) void ensurePropiedadesLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      title={
        <span>
          Registrar movimiento
          {aseadorNombre && (
            <Text type="secondary" style={{ marginLeft: 8, fontWeight: 400 }}>
              — {aseadorNombre}
            </Text>
          )}
        </span>
      }
      onCancel={onClose}
      onOk={handleOk}
      okText="Registrar (queda pendiente)"
      cancelText="Cancelar"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          signo: 1,
          fecha: ahoraChile(),
          concepto: "adelanto" as ConceptoAseador,
          contraparteKind: "af" as ContraparteKind,
        }}
      >
        <Form.Item
          name="concepto"
          label="Concepto"
          rules={[{ required: true, message: "Elige un concepto" }]}
        >
          <Select
            onChange={handleConceptoChange}
            options={CONCEPTO_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </Form.Item>

        {conceptoHint && (
          <Text type="secondary" style={{ display: "block", marginTop: -8, marginBottom: 16 }}>
            {conceptoHint}
          </Text>
        )}

        <Form.Item
          name="signo"
          label="Dirección del flujo"
          rules={[{ required: true }]}
          tooltip="Aseador recibe = el dinero entra a su cuenta. Aseador paga = sale de su cuenta (multa, devolución a AF)."
        >
          <Radio.Group>
            <Radio value={1}>Aseador recibe (+)</Radio>
            <Radio value={-1}>Aseador paga (−)</Radio>
          </Radio.Group>
        </Form.Item>

        {/* ── Contraparte ─────────────────────────────────────────────
            "¿Quién está al otro lado de la transacción?" Sin este campo
            el sistema asume AF en ambas direcciones — pero hay flujos
            donde AF no participa (ej: el propietario paga al aseador
            con AF de intermediario; o aseador devuelve plata a un
            externo). Cada kind tiene un comportamiento distinto en el
            ledger — ver el JSDoc del modal y el backend
            `crearMovimientoAseador`. */}
        <Form.Item
          name="contraparteKind"
          label="Contraparte"
          rules={[{ required: true }]}
          tooltip={
            <>
              <div>
                <b>Aloja Fácil</b>: AF pone (o recibe) el dinero. Movimiento
                contable normal — entra a la utilidad AF.
              </div>
              <div style={{ marginTop: 6 }}>
                <b>Propiedad / Cliente</b>: el dinero pasa entre el
                propietario de la propiedad y el aseador, AF actúa de
                intermediario. Aparece en la liquidación al cliente
                (<code>/finanzas-clientes</code>) y NO afecta la utilidad
                AF (rol intermediario).
              </div>
              <div style={{ marginTop: 6 }}>
                <b>Externo</b>: tercero ajeno (caja chica, proveedor,
                conocido). Solo se registra para auditoría — AF no gana ni
                pierde plata.
              </div>
            </>
          }
        >
          <Radio.Group onChange={(e) => handleContraparteChange(e.target.value)}>
            <Radio value="af">Aloja Fácil</Radio>
            <Radio value="propiedad">Propiedad / Cliente</Radio>
            <Radio value="externo">Externo</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Select de propiedad — solo visible cuando contraparte=propiedad. */}
        {contraparteKind === "propiedad" && (
          <Form.Item
            name="contrapartePropiedadId"
            label="¿Qué propiedad?"
            rules={[
              { required: true, message: "Elige la propiedad asociada" },
            ]}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {signoActual === 1
                  ? "El cliente dueño de esta propiedad será cargado por el monto en su próxima liquidación."
                  : "El aseador devuelve plata al cliente dueño — se reflejará como abono en la liquidación de la propiedad."}
              </Text>
            }
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona una propiedad"
              loading={loadingProps}
              options={propiedades}
            />
          </Form.Item>
        )}

        {/* Input de nombre — solo cuando contraparte=externo. Default
            "Externo" si admin lo deja vacío. */}
        {contraparteKind === "externo" && (
          <Form.Item
            name="contraparteNombreExterno"
            label="Nombre del externo (opcional)"
            rules={[{ max: 120 }]}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Solo se guarda como etiqueta en el ledger — no crea ni busca
                un usuario en el sistema. Ej: "Caja chica", "Proveedor X".
              </Text>
            }
          >
            <Input placeholder="Ej: Caja chica" maxLength={120} />
          </Form.Item>
        )}

        <Form.Item
          name="monto"
          label="Monto (CLP)"
          rules={[
            { required: true, message: "Ingresa un monto" },
            {
              validator: (_, v) =>
                Number.isFinite(v) && v > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("Debe ser > 0")),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={1}
            step={1000}
            formatter={(v) =>
              `$ ${String(v ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`
            }
            parser={
              ((v: string | undefined) =>
                Number(String(v ?? "").replace(/[^\d]/g, "")) || 0) as any
            }
          />
        </Form.Item>

        <Form.Item
          name="fecha"
          label="Fecha del movimiento"
          rules={[{ required: true, message: "Elige una fecha" }]}
        >
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item
          name="descripcion"
          label="Descripción / observación"
          rules={[
            { required: true, message: "Escribe una descripción breve" },
            { max: 240 },
          ]}
        >
          <Input.TextArea
            rows={2}
            placeholder='Ej: "Adelanto del 30% del mes" / "Multa por inasistencia 25-04"'
          />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: 12 }}>
          El movimiento queda en estado <strong>Pendiente</strong>. Apruébalo
          desde el detalle del aseador (o el lunes el sistema te recordará).
          {contraparteKind === "af" && (
            <>
              {" "}
              También aparecerá en <code>/finanzas-empresa</code> bajo "Sueldos"
              ya que comparte la misma tabla contable.
            </>
          )}
          {contraparteKind === "propiedad" && (
            <>
              {" "}
              Aparecerá además en la liquidación al cliente de esa propiedad
              (<code>/finanzas-clientes</code>). NO afectará la utilidad AF
              porque queda como rol "intermediario".
            </>
          )}
          {contraparteKind === "externo" && (
            <>
              {" "}
              Solo queda registrado para auditoría — no afecta utilidad AF
              ni liquidaciones a clientes.
            </>
          )}
        </Text>
      </Form>
    </Modal>
  );
}
